/**
 * Every call the SPA makes, named and typed.
 *
 * Pages import from here rather than composing URLs, so a path only ever appears once
 * and a route rename is a compile error instead of a 404 someone finds later.
 */

import { api } from "./client";
import type { Schemas } from "./client";

export type Me = Schemas["MeOut"];
export type Project = Schemas["ProjectSummary"];
export type ProjectDetail = Schemas["ProjectOut"];
export type Member = Schemas["MemberOut"];
export type AdminUser = Schemas["UserOut"];
export type Invitation = Schemas["InvitationOut"];
export type BundleUploaded = Schemas["BundleUploaded"];
export type SignInResponse = Schemas["SignInResponse"];
export type Script = Schemas["ScriptOut"];
export type ScriptDetail = Schemas["ScriptDetail"];
export type ScriptVersion = Schemas["ScriptVersionOut"];
export type Build = Schemas["BuildOut"];
export type BuildSummary = Schemas["BuildSummary"];
export type Run = Schemas["RunSummary"];
export type RunDetail = Schemas["RunDetail"];
export type RunCreated = Schemas["RunCreated"];
export type Artifact = Schemas["ArtifactOut"];
export type UploadTarget = Schemas["UploadTarget"];
export type ValidationResult = Schemas["ValidationResultOut"];
export type Schedule = Schemas["ScheduleSummary"];
export type Repo = Schemas["RepoOut"];
export type SyncResult = Schemas["SyncOut"];
export type UnregisteredScript = Schemas["UnregisteredScriptOut"];
export type ManifestDraft = Schemas["DraftOut"];
export type AuditEvent = Schemas["AuditEventOut"];
export type Notification = Schemas["NotificationOut"];
export type Role = Schemas["Role"];
export type RunStatus = Schemas["RunStatus"];
export type ProjectSecret = Schemas["ProjectSecretOut"];
export type TeamsConnection = Schemas["ConnectionOut"];
export type TeamsTarget = Schemas["TargetOut"];
export type TeamsTargetIn = Schemas["TargetIn"];
// A chat/channel available to add — not yet a chosen destination, so it has no id.
export type TeamsTargetOption = Schemas["TargetOptionOut"];


type Page<T> = { items: T[]; total: number; limit: number; offset: number };

const project = (ref: string) => `/api/projects/${encodeURIComponent(ref)}`;

export const endpoints = {
  // -- identity -------------------------------------------------------------------
  me: () => api.get<Me | null>("/api/auth/me"),
  signIn: (email: string, password: string) =>
    api.post<SignInResponse>("/api/auth/login", { email, password }),
  logout: () => api.post<{ status: string }>("/api/auth/logout"),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<Me>("/api/auth/change-password", {
      current_password: currentPassword,
      new_password: newPassword,
    }),
  acceptInvitation: (body: Schemas["AcceptInvitationRequest"]) =>
    api.post<SignInResponse>("/api/auth/accept-invitation", body),
  // Not schema-generated (/api/ready has no response_model) — checks.sso tells the sign-in
  // screen whether to offer "Continue with Microsoft".
  ready: () =>
    api.get<{ status: string; environment: string; checks: Record<string, string> }>(
      "/api/ready",
    ),

  // -- projects -------------------------------------------------------------------
  projects: (query?: { limit?: number; offset?: number; include_archived?: boolean }) =>
    api.get<Page<Project>>("/api/projects", query),
  project: (ref: string) => api.get<ProjectDetail>(project(ref)),
  createProject: (body: Schemas["ProjectCreate"]) =>
    api.post<ProjectDetail>("/api/projects", body),
  updateProject: (ref: string, body: Schemas["ProjectUpdate"]) =>
    api.patch<ProjectDetail>(project(ref), body),

  members: (ref: string) => api.get<Member[]>(`${project(ref)}/members`),
  upsertMember: (ref: string, body: Schemas["MemberUpsert"]) =>
    api.put<Member>(`${project(ref)}/members`, body),
  removeMember: (ref: string, userId: string) =>
    api.delete<void>(`${project(ref)}/members/${userId}`),

  // -- secrets ----------------------------------------------------------------------
  secrets: (ref: string) => api.get<ProjectSecret[]>(`${project(ref)}/secrets`),
  putSecret: (ref: string, name: string, value: string) =>
    api.put<ProjectSecret>(`${project(ref)}/secrets/${encodeURIComponent(name)}`, {
      name,
      value,
    }),
  removeSecret: (ref: string, name: string) =>
    api.delete<void>(`${project(ref)}/secrets/${encodeURIComponent(name)}`),

  // -- accounts (platform admin) ---------------------------------------------------

  users: (query?: { search?: string; limit?: number; offset?: number }) =>
    api.get<Page<AdminUser>>("/api/admin/users", query),
  inviteUser: (body: Schemas["InviteRequest"]) =>
    api.post<Invitation>("/api/admin/users/invite", body),
  resetUserPassword: (userId: string) =>
    api.post<Invitation>(`/api/admin/users/${userId}/reset-password`),
  updateUser: (userId: string, body: Schemas["UpdateUserRequest"]) =>
    api.patch<AdminUser>(`/api/admin/users/${userId}`, body),
  userInvitations: (userId: string) =>
    api.get<
      Array<{
        id: string;
        created_at: string;
        expires_at: string;
        accepted_at: string | null;
        revoked_at: string | null;
        is_open: boolean;
      }>
    >(`/api/admin/users/${userId}/invitations`),

  // -- scripts --------------------------------------------------------------------
  scripts: (ref: string, query?: { include_archived?: boolean }) =>
    api.get<Page<Script>>(`${project(ref)}/scripts`, query),
  script: (ref: string, scriptRef: string) =>
    api.get<ScriptDetail>(`${project(ref)}/scripts/${encodeURIComponent(scriptRef)}`),
  updateScript: (ref: string, scriptRef: string, body: Schemas["ScriptUpdate"]) =>
    api.patch<Script>(`${project(ref)}/scripts/${encodeURIComponent(scriptRef)}`, body),
  archiveScript: (ref: string, scriptRef: string) =>
    api.post<Script>(`${project(ref)}/scripts/${encodeURIComponent(scriptRef)}/archive`),

  versions: (ref: string, scriptRef: string) =>
    api.get<ScriptVersion[]>(`${project(ref)}/scripts/${encodeURIComponent(scriptRef)}/versions`),
  promoteVersion: (ref: string, scriptRef: string, versionId: string) =>
    api.post<ScriptVersion>(
      `${project(ref)}/scripts/${encodeURIComponent(scriptRef)}/versions/${versionId}/promote`,
    ),
  buildVersion: (ref: string, scriptRef: string, versionId: string) =>
    api.post<Build>(
      `${project(ref)}/scripts/${encodeURIComponent(scriptRef)}/versions/${versionId}/build`,
    ),
  builds: (ref: string, scriptRef: string, versionId: string) =>
    api.get<Build[]>(
      `${project(ref)}/scripts/${encodeURIComponent(scriptRef)}/versions/${versionId}/builds`,
    ),
  // Every build attempt for a script, across all its versions — what the script's
  // "Builds" tab shows, so a failure is not hidden behind a version nobody has open.
  scriptBuilds: (ref: string, scriptRef: string) =>
    api.get<BuildSummary[]>(`${project(ref)}/scripts/${encodeURIComponent(scriptRef)}/builds`),
  build: (ref: string, scriptRef: string, buildId: string) =>
    api.get<Build>(`${project(ref)}/scripts/${encodeURIComponent(scriptRef)}/builds/${buildId}`),
  overrideScan: (ref: string, scriptRef: string, buildId: string, reason: string) =>
    api.post<Build>(
      `${project(ref)}/scripts/${encodeURIComponent(scriptRef)}/builds/${buildId}/override-scan`,
      { reason },
    ),
  buildLogs: (ref: string, scriptRef: string, buildId: string, nextToken?: string) =>
    api.get<Schemas["BuildLogPage"]>(
      `${project(ref)}/scripts/${encodeURIComponent(scriptRef)}/builds/${buildId}/logs`,
      { next_token: nextToken },
    ),

  // -- runs -----------------------------------------------------------------------
  runs: (
    ref: string,
    query?: { script_ref?: string; status?: string; limit?: number; offset?: number },
  ) => api.get<Page<Run>>(`${project(ref)}/runs`, query),
  run: (ref: string, runId: string) => api.get<RunDetail>(`${project(ref)}/runs/${runId}`),
  createRun: (ref: string, scriptRef: string, body: Schemas["RunCreate"]) =>
    api.post<RunCreated>(`${project(ref)}/scripts/${encodeURIComponent(scriptRef)}/runs`, body),
  startRun: (ref: string, runId: string) =>
    api.post<Schemas["RunOut"]>(`${project(ref)}/runs/${runId}/start`),
  cancelRun: (ref: string, runId: string, reason?: string) =>
    api.post<Schemas["RunOut"]>(`${project(ref)}/runs/${runId}/cancel`, { reason }),
  rerun: (ref: string, runId: string) =>
    api.post<RunCreated>(`${project(ref)}/runs/${runId}/rerun`),
  logs: (ref: string, runId: string, nextToken?: string) =>
    api.get<Schemas["LogPage"]>(`${project(ref)}/runs/${runId}/logs`, {
      next_token: nextToken,
    }),
  /** Authorise and audit every file of a run, returning a signed token to stream them. */
  prepareArchive: (ref: string, runId: string, reason?: string, kind?: "output" | "input") =>
    api.post<ArchiveGrant>(`${project(ref)}/runs/${runId}/artifacts/archive`, { reason, kind }),

  /** Stream the files covered by a grant token as a ZIP download. */
  streamArchiveUrl: (ref: string, runId: string) =>
    `${project(ref)}/runs/${runId}/artifacts/archive/stream`,

  /** Build a stored ZIP of the run's files, downloaded from S3 like any other file. */
  bundleArtifacts: (ref: string, runId: string, reason?: string, kind?: "output" | "input") =>
    api.post<BundleStarted>(`${project(ref)}/runs/${runId}/artifacts/bundle`, { reason, kind }),

  downloadArtifact: (ref: string, runId: string, artifactId: string, reason?: string) =>
    api.post<Schemas["DownloadOut"]>(
      `${project(ref)}/runs/${runId}/artifacts/${artifactId}/download`,
      { reason },
    ),

  // -- schedules ------------------------------------------------------------------
  schedules: (ref: string, query?: { script_ref?: string }) =>
    api.get<Schedule[]>(`${project(ref)}/schedules`, query),
  createSchedule: (ref: string, scriptRef: string, body: Schemas["ScheduleCreate"]) =>
    api.post<Schedule>(
      `${project(ref)}/scripts/${encodeURIComponent(scriptRef)}/schedules`,
      body,
    ),
  updateSchedule: (ref: string, id: string, body: Schemas["ScheduleUpdate"]) =>
    api.patch<Schedule>(`${project(ref)}/schedules/${id}`, body),
  deleteSchedule: (ref: string, id: string) =>
    api.delete<void>(`${project(ref)}/schedules/${id}`),

  // -- repositories and onboarding --------------------------------------------------
  repos: (ref: string) => api.get<Repo[]>(`${project(ref)}/repos`),
  connectRepo: (ref: string, body: Schemas["RepoConnect"]) =>
    api.post<Repo>(`${project(ref)}/repos`, body),
  syncRepo: (ref: string, repoId: string) =>
    api.post<SyncResult>(`${project(ref)}/repos/${repoId}/sync`),
  unregistered: (ref: string, repoId: string) =>
    api.get<UnregisteredScript[]>(`${project(ref)}/repos/${repoId}/unregistered`),
  dismissUnregistered: (ref: string, repoId: string, id: string) =>
    api.post<UnregisteredScript>(
      `${project(ref)}/repos/${repoId}/unregistered/${id}/dismiss`,
    ),
  draftManifest: (ref: string, repoId: string, body: Schemas["DraftRequest"]) =>
    api.post<ManifestDraft>(`${project(ref)}/repos/${repoId}/manifest/draft`, body),
  validateManifest: (ref: string, repoId: string, yaml: string) =>
    api.post<Schemas["ValidateOut"]>(`${project(ref)}/repos/${repoId}/manifest/validate`, {
      yaml,
    }),
  commitManifest: (ref: string, repoId: string, body: Schemas["CommitRequest"]) =>
    api.post<Schemas["CommitOut"]>(`${project(ref)}/repos/${repoId}/manifest/commit`, body),

  // -- audit and notifications ------------------------------------------------------
  audit: (
    ref: string,
    query?: { action?: string; actor_email?: string; run_id?: string; limit?: number; offset?: number },
  ) => api.get<Page<AuditEvent>>(`${project(ref)}/audit`, query),
  platformAudit: (query?: { action?: string; actor_email?: string; limit?: number }) =>
    api.get<Page<AuditEvent>>("/api/admin/audit", query),
  notifications: (query?: { unread_only?: boolean; limit?: number }) =>
    api.get<Page<Notification>>("/api/notifications", query),
  unreadCount: () => api.get<{ unread: number }>("/api/notifications/unread-count"),
  markRead: (id: string) => api.post<Notification>(`/api/notifications/${id}/read`),
  markAllRead: () => api.post<{ unread: number }>("/api/notifications/read-all"),

  // -- your own Microsoft Teams connection -------------------------------------------
  teamsConnection: () => api.get<TeamsConnection>("/api/me/teams"),
  // Chats/channels available to add — live from Graph, not yet chosen destinations.
  teamsTargetOptions: () => api.get<TeamsTargetOption[]>("/api/me/teams/targets"),
  // Omit scriptId for the user's default set; pass it to resolve what a specific script
  // would actually use (its own override set if it has one, falling back to the default).
  teamsTargetSet: (scriptId?: string) =>
    api.get<TeamsTarget[]>(
      "/api/me/teams/target-set",
      scriptId ? { script_id: scriptId } : undefined,
    ),
  addTeamsTarget: (body: TeamsTargetIn) => api.post<TeamsTarget>("/api/me/teams/target", body),
  removeTeamsTarget: (targetId: string) =>
    api.delete<void>(`/api/me/teams/target/${encodeURIComponent(targetId)}`),
  // Removes every destination in a script's override set, reverting it to the default —
  // never clears the default set itself.
  clearTeamsTargetOverrides: (scriptId: string) =>
    api.delete<void>(`/api/me/teams/target?script_id=${encodeURIComponent(scriptId)}`),
  disconnectTeams: () => api.delete<void>("/api/me/teams"),
};

/**
 * Upload one input file straight to S3 with the presigned POST the API issued.
 *
 * The bytes never pass through the API, which is what makes a multi-gigabyte export
 * practical.
 */
export async function uploadToS3(target: UploadTarget, file: File): Promise<void> {
  const form = new FormData();
  for (const [key, value] of Object.entries(target.fields)) {
    form.append(key, value);
  }
  // S3 requires the file last: everything after it in the form is ignored.
  form.append("file", file);

  const response = await fetch(target.url, { method: "POST", body: form });
  if (!response.ok) {
    throw new Error(
      response.status === 403
        ? `${file.name} was refused by storage — it may be larger than the manifest allows.`
        : `${file.name} could not be uploaded (HTTP ${response.status}).`,
    );
  }
}

/**
 * Upload a script bundle.
 *
 * Multipart rather than a presigned POST like run inputs: the API has to open the
 * archive and validate its manifest before anything is stored, so a rejected bundle
 * leaves nothing behind.
 */
export async function uploadBundle(
  projectRef: string,
  file: File,
  options: { manifestPath?: string; manifestYaml?: string } = {},
): Promise<BundleUploaded> {
  const form = new FormData();
  form.append("file", file);
  if (options.manifestPath) form.append("manifest_path", options.manifestPath);
  // A manifest written in the wizard, for an archive that has none of its own.
  if (options.manifestYaml) form.append("manifest_yaml", options.manifestYaml);

  return postForm<BundleUploaded>(`/api/projects/${encodeURIComponent(projectRef)}/bundles`, form);
}

export interface ArchiveGrant {
  token: string;
  filename: string;
  file_count: number;
  size_bytes: number;
  /** Left out because they failed validation and the caller cannot release them. */
  excluded: string[];
  expires_in_seconds: number;
}

export interface BundleStarted {
  status: string;
  filename: string;
  file_count: number;
  excluded: string[];
}

export interface BundleDraft {
  candidates: string[];
  entrypoint: string;
  yaml: string;
  manifest: Record<string, unknown>;
  framework: string | null;
  detected: Array<{
    name: string;
    type: string;
    arg?: string | null;
    required?: boolean;
    confidence?: string;
  }>;
  warnings: string[];
  existing_manifest_path: string | null;
}

/**
 * Ask the wizard to propose a manifest for a script in an archive that has none.
 *
 * Nothing is stored: the archive is uploaded again with the finished manifest. That
 * costs a second upload of a small file, and keeps a rejected draft from leaving
 * anything behind.
 */
export async function draftBundleManifest(
  projectRef: string,
  file: File,
  options: { entrypoint?: string; scriptName?: string } = {},
): Promise<BundleDraft> {
  const form = new FormData();
  form.append("file", file);
  if (options.entrypoint) form.append("entrypoint", options.entrypoint);
  if (options.scriptName) form.append("script_name", options.scriptName);

  return postForm<BundleDraft>(`/api/projects/${encodeURIComponent(projectRef)}/bundles/draft`, form);
}

export function validateBundleManifest(projectRef: string, yaml: string) {
  return api.post<{ valid: boolean; manifest: Record<string, unknown> | null; error: string | null }>(
    `/api/projects/${encodeURIComponent(projectRef)}/bundles/validate-manifest`,
    { yaml },
  );
}

async function postForm<T>(path: string, form: FormData): Promise<T> {
  const response = await fetch(path, {
    method: "POST",
    credentials: "same-origin",
    body: form,
  });

  const text = await response.text();
  let payload: unknown = text;
  try {
    payload = JSON.parse(text);
  } catch {
    // Leave it as text.
  }

  if (!response.ok) {
    const detail =
      payload && typeof payload === "object" && "detail" in payload
        ? String((payload as { detail: unknown }).detail)
        : `That did not work (HTTP ${response.status}).`;
    throw new Error(detail);
  }

  return payload as T;
}
